import { type Editor } from '@tiptap/react';

/**
 * The `<a>` under the pointer, if the event target sits inside a link mark in
 * the editor content. Returns null for non-element targets or plain text.
 */
export const findLinkAnchorFromEvent = (
  target: EventTarget | null
): HTMLAnchorElement | null =>
  target instanceof HTMLElement ? target.closest('a') : null;

/**
 * The `<a>` the collapsed caret sits inside, or null. Powers the keyboard route
 * to the link menu: the bubble-menu's ⌘K needs a selection, so a caret resting
 * in a link would otherwise have no way to reach Open / Edit / Remove.
 */
export const getCaretLinkAnchor = (editor: Editor): HTMLAnchorElement | null => {
  const { selection } = editor.state;
  if (!selection.empty || !editor.isActive('link')) {
    return null;
  }
  const dom = editor.view.domAtPos(selection.from).node;
  const el = dom instanceof HTMLElement ? dom : dom.parentElement;
  return el?.closest('a') ?? null;
};

/**
 * Moves the caret into `anchor` and extends the selection to cover the whole
 * link mark. In hover mode the caret can be anywhere, so Edit / Remove must
 * retarget the hovered link before running their command — otherwise they act
 * on wherever the caret happened to rest.
 *
 * Returns false when the anchor is no longer in the document (a live
 * `setContent` from streaming / autosave / chat inserts can detach it while the
 * card is open); callers must bail rather than mutate the current selection.
 */
export const selectLink = (editor: Editor, anchor: HTMLAnchorElement): boolean => {
  const pos = editor.view.posAtDOM(anchor, 0);
  if (pos < 0) {
    return false;
  }
  return editor
    .chain()
    .focus()
    .setTextSelection(pos + 1)
    .extendMarkRange('link')
    .run();
};
